<script>
import {
  Bilety24EventDate,
  Category,
  DefaultEventTicket,
  Event,
  EventTag,
  Place
} from '@/api'
import PageFormBase from '@/crudBases/form/PageFormBase'
import fields from '@/components/fields/'
import moment from 'moment'
import { get } from 'lodash'

export default {
  mixins: [PageFormBase],
  data() {
    return {
      form: {
        widgets: [],
        dates: [],
        creators: [],
        tickets: [],
        reviews: []
      },
      tagOptions: [],
      redirectRoute: { name: 'Event List' },
      livePreview: true
    }
  },
  computed: {
    service() {
      return Event
    },
    currentLanguage() {
      return this.$store.state.selectedLanguage
    },
    livePreviewUrl() {
      return '/preview/events/'
    },
    isSpectacle() {
      return this.form.type === 'spectacle'
    }
  },
  created() {
    this.fetchTags()

    if (!this.lookupParams && !this.$route.query.bilety24_event_date) {
      this.fetchDefaultTickets()
    }
    if (!this.lookupParams && this.$route.query.bilety24_event_date) {
      this.fetchBilety24Instance()
    }
  },
  methods: {
    fetchTags() {
      const params = { page_size: 'max' }
      EventTag.fetch({}, { params }).then(res => {
        this.tagOptions = res.data.results.map(item => ({
          label: item.name,
          value: item.name
        }))
      })
    },
    async fetchBilety24Instance() {
      const [
        bilety24Response,
        defaultEventTicketsResponse
      ] = await Promise.all([
        Bilety24EventDate.get(this.$route.query.bilety24_event_date),
        DefaultEventTicket.fetch()
      ])
      const bilety24Data = bilety24Response.data

      const tickets = defaultEventTicketsResponse.data.tickets.map(item => ({
        ...item,
        id: undefined,
        event: undefined
      }))

      this.form = {
        ...this.form,
        type: bilety24Data.type,
        bilety24_title_id: bilety24Data.bilety24_title_id,
        bilety24_event_date: bilety24Data.id,
        title_pl: bilety24Data.title_pl,
        title_en: bilety24Data.title_en,
        description_pl: bilety24Data.description_pl,
        description_en: bilety24Data.description_en,
        image: bilety24Data.image,
        horizontal_image: bilety24Data.image,
        dates: bilety24Data.dates,
        tickets
      }
    },
    fetchDefaultTickets() {
      DefaultEventTicket.fetch().then(res => {
        this.form.tickets = res.data.tickets.map(item => ({
          ...item,
          id: undefined,
          event: undefined
        }))
      })
    },
    getFields() {
      const children = [
        this.getGeneralTab(),
        this.getDatesTab(),
        this.getTicketsTab(),
        this.getWidgetsTab(),
        this.getPreviewTab(),
        this.getSEOTab()
      ]
      if (this.isSpectacle) {
        children.splice(2, 0, this.getCreatorsTab())
        children.splice(4, 0, this.getReviewsTab())
      }
      return [
        {
          component: 'el-tabs',
          children
        }
      ]
    },
    getGeneralTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Ogólne'
        },
        children: [
          new fields.Select({
            modelPath: [this, 'form', 'type'],
            required: true,
            clearable: false,
            label: 'Typ wydarzenia',
            options: [
              { label: 'Standardowe wydarzenie', value: 'standard' },
              { label: 'Spektakl', value: 'spectacle' }
            ]
          }),
          new fields.Input({
            label: 'Tytuł',
            modelPath: [this, 'form', 'title'],
            type: 'textarea',
            multilingual: true,
            required: true,
            copyTo: {
              og_title_pl: {
                stripHtml: false,
                maxLength: 150,
                sameLanguage: true
              },
              og_title_en: {
                stripHtml: false,
                maxLength: 150,
                sameLanguage: true
              },
              meta_title_pl: { stripHtml: false, sameLanguage: true },
              meta_title_en: { stripHtml: false, sameLanguage: true }
            }
          }),
          new fields.Input({
            label: 'Podpis',
            type: 'textarea',
            modelPath: [this, 'form', 'subtitle'],
            multilingual: true,
            help: this.isSpectacle
              ? 'Podpis pojawia się w kalendarium pod tytułem spektaklu'
              : 'Podpis pojawia się w kalendarium pod tytułem wydarzenia i w nagłówku na podstronie wydarzenia'
          }),
          new fields.WYSIWYG({
            label: 'Opis',
            modelPath: [this, 'form', 'description'],
            multilingual: true,
            required: true,
            copyTo: {
              og_description_pl: {
                stripHtml: true,
                maxLength: 256,
                sameLanguage: true
              },
              meta_description_pl: { stripHtml: true, sameLanguage: true },
              og_description_en: {
                stripHtml: true,
                maxLength: 256,
                sameLanguage: true
              },
              meta_description_en: { stripHtml: true, sameLanguage: true }
            }
          }),
          new fields.WYSIWYG({
            label: 'Informacje',
            modelPath: [this, 'form', 'info'],
            multilingual: true,
            condition: !this.isSpectacle
          }),
          new fields.Select({
            label: 'Tagi',
            modelPath: [this, 'form', 'tags'],
            multiple: true,
            filterable: true,
            allowCreate: true,
            options: this.tagOptions
          }),
          new fields.Image({
            label: 'Zdjęcie pionowe',
            modelPath: [this, 'form', 'image'],
            required: true,
            copyTo: { og_image: { stripHtml: false } }
          }),
          new fields.Image({
            label: 'Zdjęcie poziome',
            modelPath: [this, 'form', 'horizontal_image'],
            required: true,
            help:
              'Zdjęcie pojawi się w nagłówku spektaklu oraz innych widgetach na desktop. Sugerowany rozmiar: 1280x370'
          }),
          new fields.Date({
            label: 'Data premiery',
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            modelPath: [this, 'form', 'premiere_date']
          }),
          new fields.ForeignKey({
            label: 'Kategorie',
            modelPath: [this, 'form', 'categories'],
            labelField: 'name_pl',
            valueField: 'id',
            clearable: true,
            multiple: true,
            service: Category
          }),
          new fields.Switch({
            label: 'Ukryte',
            modelPath: [this, 'form', 'is_hidden']
          }),
          new fields.Input({
            label: 'Link do trailera',
            modelPath: [this, 'form', 'trailer'],
            condition: this.isSpectacle
          }),
          new fields.Input({
            label: 'Link do biletów',
            modelPath: [this, 'form', 'ticket_url'],
            help:
              'Link będzie wyświetlał się w szczegółach wydarzenia w zakładce bilety. ' +
              'W przypadku pozostawienia pustego pola link zostanie automatycznie wygenerowany do wydarzenia na bilety24 (jeżeli wydarzenie zostanie poprawnie zsynchronizowane)'
          }),
          new fields.Input({
            label: this.isSpectacle ? 'Reżyser' : 'Autor',
            modelPath: [this, 'form', 'director']
          }),
          new fields.Input({
            label: 'Scenarzysta',
            modelPath: [this, 'form', 'writer'],
            condition: this.isSpectacle
          }),
          new fields.Input({
            label: 'Czas trwania',
            modelPath: [this, 'form', 'duration'],
            multilingual: true,
            condition: this.isSpectacle
          }),
          new fields.Link({
            label: 'Link do wydarzenia',
            modelPath: [this, 'form', 'pinned_link'],
            condition: !this.isSpectacle
          }),
          new fields.Input({
            label: 'Link do YT',
            modelPath: [this, 'form', 'background_video_url'],
            small: 'Wyświetla się w widgecie zapowiedzi, w tle'
          })
        ]
      }
    },
    getDatesTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Daty'
        },
        children: [this.getDatesFormset()]
      }
    },
    getWidgetsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Widgety'
        },
        children: [new fields.Widgets({ modelPath: [this, 'form', 'widgets'] })]
      }
    },
    getSEOTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'SEO'
        },
        children: [
          new fields.Input({
            label: 'Slug',
            modelPath: [this, 'form', 'slug'],
            multilingual: true,
            condition: !!this.form.id
          }),
          new fields.Input({
            label: 'Tytuł dla facebooka',
            modelPath: [this, 'form', 'og_title'],
            multilingual: true
          }),
          new fields.Input({
            label: 'Opis dla facebooka',
            modelPath: [this, 'form', 'og_description'],
            type: 'textarea',
            multilingual: true
          }),
          new fields.Image({
            label: 'Zdjęcie dla facebooka',
            modelPath: [this, 'form', 'og_image']
          }),
          new fields.Input({
            label: 'Opis dla wyszukiwarki google',
            modelPath: [this, 'form', 'meta_description'],
            type: 'textarea',
            multilingual: true
          }),
          new fields.Input({
            label: 'Tytuł strony dla wyszukiwarki google',
            modelPath: [this, 'form', 'meta_title'],
            multilingual: true
          })
        ]
      }
    },
    getDatesFormset() {
      return new fields.Formset({
        modelPath: [this, 'form', 'dates'],
        items: this.form.dates.map((item, i) => {
          return {
            headerLabel: moment(item.date).format('YYYY-MM-DD HH:mm:ss'),
            fields: [
              new fields.ForeignKey({
                label: 'Miejsce',
                modelPath: [this, 'form', 'dates', i, 'place'],
                labelField: 'name_pl',
                valueField: 'id',
                clearable: true,
                service: Place
              }),
              new fields.Select({
                label: 'Status biletów',
                // prettier-ignore
                modelPath: [this, 'form', 'dates', i, 'ticket_status'],
                options: [
                  { value: 'available', label: 'Kup bilet' },
                  { value: 'unavailable', label: 'Brak biletów' },
                  { value: 'free', label: 'Wstęp wolny' },
                  { value: 'ticket_office', label: 'Bilety w kasach' }
                ]
              }),
              new fields.Date({
                label: 'Data i godzina',
                // prettier-ignore
                modelPath: [this, 'form', 'dates', i, 'date'],
                type: 'datetime',
                required: true
              }),
              new fields.Switch({
                label: 'Wydarzenie zostalo odwołane',
                modelPath: [this, 'form', 'dates', i, 'is_cancelled']
              }),
              new fields.Switch({
                label: 'Premiera',
                modelPath: [this, 'form', 'dates', i, 'is_premiere']
              }),
              new fields.Switch({
                label: 'Online',
                modelPath: [this, 'form', 'dates', i, 'is_online']
              }),
              new fields.Input({
                label: 'Link do biletów',
                modelPath: [this, 'form', 'dates', i, 'external_ticket_url'],
                multilingual: true
              })
            ]
          }
        })
      })
    },
    getCreatorsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Twórcy'
        },
        children: [this.getCreatorsFormset()]
      }
    },
    getCreatorsFormset() {
      return new fields.Formset({
        modelPath: [this, 'form', 'creators'],
        items: this.form.creators.map((item, i) => {
          return {
            headerLabel: get(item, ['name_pl']),
            fields: [
              new fields.Input({
                label: 'Nazwa',
                modelPath: [this, 'form', 'creators', i, 'name'],
                multilingual: true,
                required: true
              }),
              new fields.Input({
                label: 'Rola',
                modelPath: [this, 'form', 'creators', i, 'role'],
                multilingual: true,
                required: true
              })
            ]
          }
        })
      })
    },
    getTicketsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Bilety'
        },
        children: [
          new fields.Input({
            label: 'Numer w systemie sprzedaży',
            modelPath: [this, 'form', 'bilety24_title_id'],
            help:
              'Podaj numer spektaklu w systemie sprzedaży biletów. Powinien zostać pobrany automatycznie.',
            disabled: true
          }),
          {
            component: 'div',
            attrs: {
              class: 'button-wrapper'
            },
            children: [
              this.getButtonComponent({
                label: 'Usuń połączenie z bilety24',
                onClick: () => this.clearBilety24(),
                disabled: !this.form.bilety24_title_id
              })
            ]
          },
          this.getTicketsFormset()
        ]
      }
    },
    clearBilety24() {
      this.form.bilety24_title_id = ''
      Event.clearBilety24(this.form.id)
        .then(() => {
          const message = 'Wydarzenie zostało odłączone od bilety24'
          this.$message({
            message: message,
            type: 'success'
          })
        })
        .catch(() => {
          const message = 'Nie udało się odłączyć wydarzenia od bilety24'
          this.$message({
            message: message,
            type: 'error'
          })
        })
        .finally(() => {
          this.fetchData()
        })
    },
    getTicketsFormset() {
      return new fields.Formset({
        modelPath: [this, 'form', 'tickets'],
        items: this.form.tickets.map((item, i) => {
          return {
            headerLabel: get(item, ['type_pl']),
            fields: [
              new fields.Input({
                label: 'Typ',
                modelPath: [this, 'form', 'tickets', i, 'type'],
                multilingual: true,
                required: true
              }),
              new fields.Input({
                label: 'Rodzaj miejsca',
                modelPath: [this, 'form', 'tickets', i, 'kind'],
                multilingual: true,
                required: true
              }),
              new fields.Input({
                label: 'Cena',
                modelPath: [this, 'form', 'tickets', i, 'price'],
                multilingual: true,
                required: true
              })
            ]
          }
        })
      })
    },
    getReviewsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Recenzje'
        },
        children: [this.getReviewsFormset()]
      }
    },
    getReviewsFormset() {
      return new fields.Formset({
        modelPath: [this, 'form', 'reviews'],
        items: this.form.reviews.map((item, i) => {
          return {
            headerLabel: get(item, ['title_pl']),
            fields: [
              new fields.Input({
                label: 'Autor',
                modelPath: [this, 'form', 'reviews', i, 'author'],
                multilingual: true,
                required: true
              }),
              new fields.Input({
                label: 'Tytuł',
                modelPath: [this, 'form', 'reviews', i, 'title'],
                multilingual: true,
                required: true
              }),
              new fields.Link({
                label: 'Link',
                modelPath: [this, 'form', 'reviews', i, 'link'],
                required: true,
                hideLinkLabel: true
              })
            ]
          }
        })
      })
    },
    getPreparedPayload(payload) {
      return {
        ...payload,
        creators: this.isSpectacle ? payload.creators : [],
        reviews: this.isSpectacle ? payload.reviews : [],
        pinned_link: !this.isSpectacle ? payload.pinned_link : null
      }
    }
  }
}
</script>
<style scoped lang="scss">
.button-wrapper {
  margin-bottom: 25px;
  margin-left: 100px;
}
</style>
